import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api, apiError, appReturnUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

export const Stars = ({ value = 0, size = 16 }) => (
    <span className="inline-flex gap-0.5" aria-label={`${value} étoile(s) sur 5`}>
        {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={size} className={i <= value ? "fill-gold text-gold" : "text-sage/40"} />
        ))}
    </span>
);

/**
 * Formulaire canonique de témoignage — SSOT pour le site et l'application.
 * Deux présentations, une seule logique (validation, API, consentements, modération) :
 *  - TestimonialForm : fenêtre modale (accueil du site, page Avis visiteurs web).
 *  - TestimonialFormInline : affichage direct dans la page (parcours application,
 *    sans deuxième clic ni modal supplémentaire).
 */
function TestimonialFormBody({ prefill = null, reviewToken = "", onSubmitted = null, inline = false }) {
    const [form, setForm] = useState({ name: "", email: "", rating: 0, text: "", consent_publish: false, consent_privacy: false, company: "" });
    const [state, setState] = useState({ loading: false, error: "", done: "", provenance: "site" });

    useEffect(() => {
        if (prefill) {
            setForm((f) => ({ ...f, name: prefill.name || f.name, email: prefill.email || f.email }));
        }
    }, [prefill]);

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, error: "", done: "", provenance: "site" });
        try {
            const { data } = await api.post("/public/testimonials", {
                ...form,
                rating: form.rating || null,
                review_token: reviewToken,
            });
            setState({ loading: false, error: "", done: data.detail, provenance: data.provenance || "site" });
            if (onSubmitted) onSubmitted(data.provenance || "site");
        } catch (err) {
            setState({ loading: false, error: apiError(err), done: "", provenance: "site" });
        }
    };

    return (
        <div>
            {!inline && (
                <DialogHeader>
                    <DialogTitle className="font-heading text-forest text-2xl">Partager votre expérience</DialogTitle>
                    <DialogDescription className="text-pineneedle">
                        Votre témoignage sera relu par notre équipe avant toute publication.
                    </DialogDescription>
                </DialogHeader>
            )}
            {inline && (
                <div className="mb-4">
                    <h2 className="font-heading text-forest text-2xl font-bold">Partager votre expérience</h2>
                    <p className="text-pineneedle text-sm mt-1">Votre témoignage sera relu par notre équipe avant toute publication.</p>
                </div>
            )}
            {reviewToken && (
                <p className="text-xs bg-forest/10 text-forest border border-forest/20 rounded-lg px-3 py-2 mb-4" data-testid="federated-badge">
                    Connecté depuis l'application BODYCOACHIA — vos informations autorisées ont été préremplies et restent modifiables.
                </p>
            )}
            {state.done ? (
                <div className="space-y-4">
                    <p className="text-forest bg-gold/15 border border-gold/40 rounded-lg p-4 text-sm" data-testid="testimonial-success">{state.done}</p>
                    {state.provenance === "application" && (
                        <a
                            href={appReturnUrl("success")}
                            className="flex items-center justify-center gap-2 w-full bg-forest text-ivory rounded-2xl py-3.5 font-semibold hover:bg-forest-surface transition-colors"
                            data-testid="return-to-app-button"
                        >
                            ← Retourner à l'application BODYCOACHIA
                        </a>
                    )}
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4">
                    <input type="text" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="t-name">Nom ou pseudonyme (public)</Label>
                            <Input id="t-name" data-testid="testimonial-name-input" required maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <Label htmlFor="t-email">E-mail (privé)</Label>
                            <Input id="t-email" data-testid="testimonial-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <Label>Note (facultatif)</Label>
                        <div className="flex gap-1 mt-1" data-testid="testimonial-rating-input">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <button type="button" key={i} onClick={() => setForm({ ...form, rating: form.rating === i ? 0 : i })} aria-label={`${i} étoile(s)`} data-testid={`rating-star-${i}`}>
                                    <Star size={26} className={i <= form.rating ? "fill-gold text-gold" : "text-sage/50 hover:text-gold"} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="t-text">Votre témoignage</Label>
                        <Textarea id="t-text" data-testid="testimonial-text-input" required minLength={20} maxLength={1200} rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Racontez votre expérience avec BODYCOACHIA (20 caractères minimum)" />
                    </div>
                    <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm text-pineneedle">
                            <Checkbox checked={form.consent_publish} onCheckedChange={(v) => setForm({ ...form, consent_publish: !!v })} data-testid="testimonial-consent-publish" />
                            J'autorise la publication de mon témoignage et de mon nom public sur ce site.
                        </label>
                        <label className="flex items-start gap-2 text-sm text-pineneedle">
                            <Checkbox checked={form.consent_privacy} onCheckedChange={(v) => setForm({ ...form, consent_privacy: !!v })} data-testid="testimonial-consent-privacy" />
                            J'accepte la politique de confidentialité.
                        </label>
                    </div>
                    {state.error && <p className="text-sm text-destructive" data-testid="testimonial-error">{state.error}</p>}
                    <Button type="submit" disabled={state.loading || !form.consent_publish || !form.consent_privacy} className="w-full bg-forest text-cream hover:bg-forest-surface" data-testid="testimonial-submit-button">
                        {state.loading ? "Envoi…" : "Envoyer mon témoignage"}
                    </Button>
                    {reviewToken && (
                        <a
                            href={appReturnUrl("cancelled")}
                            className="block text-center text-xs text-pineneedle underline underline-offset-4 hover:text-forest transition-colors"
                            data-testid="testimonial-cancel-return"
                        >
                            Annuler et retourner à l'application BODYCOACHIA
                        </a>
                    )}
                </form>
            )}
        </div>
    );
}

export function TestimonialForm({ open, onClose, prefill = null, reviewToken = "", onSubmitted = null }) {
    const close = () => onClose();
    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="bg-cream border-gold/30 max-w-lg" data-testid="testimonial-form-dialog">
                <TestimonialFormBody prefill={open ? prefill : null} reviewToken={reviewToken} onSubmitted={onSubmitted} />
            </DialogContent>
        </Dialog>
    );
}

/** Présentation directe (parcours application) : le même formulaire canonique,
 * affiché immédiatement dans la page, sans modal ni clic supplémentaire. */
export function TestimonialFormInline({ prefill = null, reviewToken = "", onSubmitted = null }) {
    return (
        <div className="rounded-3xl bg-white border border-gold/30 shadow-sm p-6 sm:p-8" data-testid="testimonial-form-inline">
            <TestimonialFormBody prefill={prefill} reviewToken={reviewToken} onSubmitted={onSubmitted} inline />
        </div>
    );
}
